/**
 * Poll and refresh token hook.
 * This file exists to break circular dependencies between preauth-company.slice.ts and brightback-auth.slice.ts
 */

import { useCallback, useEffect } from 'react';
import { LogInPaths } from '@bbkAdminComponents/log-in-flow/log-in-paths';
import { isSignedIn } from '@bbkAdminRedux/rtkq/auth-utils';
import {
  brightbackAuthSlice,
  CONFIG_NOT_LOADED_ERROR,
  UNSUPPORTED_AUTH_SESSION_ERROR,
} from '@bbkAdminRedux/rtkq/brightback-auth.slice';
import BrightbackSession from '@bbkAdminUtils/brightback-session';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';

const getTimeRemaining = () =>
  (BrightbackSession.getAccessTokenExpiresIn() ?? 0) -
  BrightbackSession.getAccessTokenAgeInSeconds();

const getErrorMessage = (err: unknown) =>
  err instanceof Error
    ? err.message
    : typeof err === 'object' &&
        err !== null &&
        'message' in err &&
        typeof err.message === 'string'
      ? err.message
      : undefined;

export const usePollAndRefreshToken = () => {
  const [doRefreshTokens$] =
    brightbackAuthSlice.endpoints.doRefreshTokens.useMutation();
  const [signOut$] = brightbackAuthSlice.endpoints.signOut.useMutation();

  const startRefreshTimer = useCallback((timeout?: number) => {
    if (timeout) {
      console.log(
        `usePollAndRefreshToken, set up timer to refresh token in ${timeout} seconds`
      );
    }
    setTimeout(
      async () => {
        if (isSignedIn()) {
          if (BrightbackSession.getAccessTokenType() === 'dev') {
            return;
          }
          await doRefreshTokens$()
            .unwrap()
            .catch(async (err) => {
              const message = getErrorMessage(err);
              // The client config never loaded (e.g. a transient
              // /client_config failure on boot, which AdminApp already logs).
              // Both refresh and sign-out depend on it, so there is nothing
              // actionable here: skip to avoid misleading "Config is not
              // loaded" Rollbar noise and let the next poll tick retry.
              if (message === CONFIG_NOT_LOADED_ERROR) {
                return;
              }
              if (message !== UNSUPPORTED_AUTH_SESSION_ERROR) {
                GlobalLogger.error(err);
              }
              await signOut$({ redirect: LogInPaths.signIn }).unwrap();
            });
        }
        const timeRemaining = getTimeRemaining();
        startRefreshTimer(timeRemaining);
      },
      // FIXME: refactor timer to enqueue immediately if timeout has ran out or close to the end (less then 60s)
      // run refresh 30 seconds before the token expires or retry every 10 seconds
      (timeout && timeout > 60 ? timeout - 30 : 10) * 1000
    );
  }, []);

  useEffect(() => {
    startRefreshTimer();
  }, [startRefreshTimer]);
};
